import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {  Form, InputNumber } from "antd";
import CustomButton from "components/Common/ui/CustomButton";
import SettingsLayout from "../components/SettingsLayout";
import { useAbsenceSettings } from "../hooks/useAbsenceSettings";

const AbsenceSettings = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { absenceSettings: absenceSettingsState, setAbsenceSettings, loading, error, saveAbsenceSettings, fetchAbsenceSettings } = useAbsenceSettings();

  const handleThresholdChange = (value: number) => {
    setAbsenceSettings({
      ...absenceSettingsState,
      threshold: value
    });
  };

  useEffect(() => {
    fetchAbsenceSettings();
  }, []);

  const handleAlertLevelChange = (level: 'warning' | 'critical', value: number) => {
    setAbsenceSettings({
      ...absenceSettingsState,
      alertLevels: {
        ...absenceSettingsState.alertLevels,
        [level]: value
      }
    });
  };


  const handleSubmit = async () => {
    try {
      await saveAbsenceSettings({ absenceConfig: absenceSettingsState });
    } catch (error) {
      console.error("Error updating absence config:", error);
    }
  };

 

  return (
    <SettingsLayout titleKey="absence_settings" loading={loading}>

      <Form form={form} layout="vertical">
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h5 className="card-title border-bottom pb-3 mb-4">
              {t("absence_threshold")}
            </h5>

            <Form.Item
              label={<span className="fw-bold">{t("max_absences")}</span>}
              className="mb-4"
              rules={[{ required: true, message: t("validation.please_enter_max_absences") }]}
            >
              <InputNumber
                className="w-100"
                size="large"
                value={absenceSettingsState?.threshold}
                onChange={(value) => handleThresholdChange(value as number)}
                addonAfter={t("hours")}
              />
            </Form.Item>

            <h5 className="card-title border-bottom pb-3 mb-4 mt-5">
              {t("alert_levels")}
            </h5>

            <div className="row">
              <div className="col-md-6">
                <Form.Item
                  label={
                    <span className="fw-bold">
                      <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                      {t("warning_level")}
                    </span>
                  }
                  className="mb-4"
                  rules={[{ required: true, message: t("validation.please_enter_warning_level") }]}
                >
                  <InputNumber
                    className="w-100"
                    size="large"
                    min={0}
                    max={100}
                    value={absenceSettingsState?.alertLevels?.warning * 100}
                    onChange={(value) => handleAlertLevelChange('warning', (value as number) / 100)}
                    addonAfter="%"
                  />
                </Form.Item>
              </div>

              <div className="col-md-6">
                <Form.Item
                  label={
                    <span className="fw-bold">
                      <i className="fas fa-exclamation-circle text-danger me-2"></i>
                      {t("critical_level")}
                    </span>
                  }
                  className="mb-4"
                  rules={[{ required: true, message: t("validation.please_enter_critical_level") }]}
                >
                  <InputNumber
                    className="w-100"
                    size="large"
                    min={0}
                    max={100}
                    value={absenceSettingsState?.alertLevels?.critical * 100}
                    onChange={(value) => handleAlertLevelChange('critical', (value as number) / 100)}
                    addonAfter="%"
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>

        <div className="text-end">
          <CustomButton
            variant="primary"
            onClick={handleSubmit}
            className="px-5"
          >
            <i className="fas fa-save me-2"></i>
            {t("save_changes")}
          </CustomButton>
        </div>
      </Form>
    </SettingsLayout>
  );
};

export default AbsenceSettings;
